import { ThemeEvents } from "@theme/events";
import { Component } from "@theme/component";

/**
 * Sticky add-to-cart bar. Runs at every breakpoint — see the block comment in
 * blocks/floating-add-to-cart.liquid for why desktop is not already covered by
 * the sticky details column.
 *
 * It owns no product form. It finds the real add-to-cart button on the page,
 * mirrors its label/disabled state, shows itself whenever that button is off
 * screen, and forwards taps straight to it. Everything that decides what is
 * actually added — the Loop selling plan (one-time vs subscribe & save), the
 * chosen variant, the quantity, APO options — stays owned by the real button.
 *
 * Re-resolves the target on every use rather than caching it, because the Loop
 * widget swaps its button in asynchronously and again whenever the customer
 * switches between one-time and subscription.
 */
export class FloatingAddToCartComponent extends Component {
  #abortController = new AbortController();
  #observer = null;
  #watchedButton = null;
  #mutationObserver = null;
  #pendingFrame = 0;

  /**
   * Matches the wording this theme puts on the buy button in every state.
   * "Out of Stock" matters: on a sold-out product the server renders that text
   * directly, so without it here the bar would never bind to the real button
   * and would sit there showing an enabled "Add to cart" that does nothing.
   */
  static BUTTON_TEXT =
    /add (subscription )?to cart|sold out|out of stock|unavailable|notify me|pre-?order/i;

  get #label() { return this.querySelector('[ref="label"]'); }
  get #price() { return this.querySelector('[ref="price"]'); }
  get #compare() { return this.querySelector('[ref="compare"]'); }
  get #button() { return this.querySelector('[ref="button"]'); }

  /**
   * The real add-to-cart control. Skips anything inside this bar, inside a
   * quick-add dialog, or otherwise not rendered.
   * @returns {HTMLButtonElement|null}
   */
  #findMainButton() {
    const candidates = [
      ...document.querySelectorAll(
        'add-to-cart-component button, button[name="add"], .loop-widget button, [data-loop-add-to-cart]'
      ),
    ];
    const all = candidates.length
      ? candidates
      : [...document.querySelectorAll("button")];

    return (
      all.find((btn) => {
        if (this.contains(btn)) return false;
        if (btn.closest("dialog, quick-add-dialog, .quick-add-modal")) return false;
        if (!btn.offsetParent && getComputedStyle(btn).position !== "fixed") return false;
        const rect = btn.getBoundingClientRect();
        if (rect.height < 20 || rect.width < 40) return false;
        return FloatingAddToCartComponent.BUTTON_TEXT.test(btn.textContent || "");
      }) || null
    );
  }

  /**
   * Write only when the value actually changes. Every assignment in here is a
   * DOM mutation inside document.body, which is what the MutationObserver
   * below is watching — writing unconditionally re-triggers it forever and
   * takes the renderer down with it.
   */
  #setText(el, value) {
    if (el && value && el.textContent !== value) el.textContent = value;
  }

  #setHidden(el, hidden) {
    if (el && el.hidden !== hidden) el.hidden = hidden;
  }

  /** "$1,234.56" -> 1234.56, so we can reject nonsense like Loop's $0.00. */
  #amount(text) {
    const n = parseFloat((text || "").replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  /** Copy the real button's wording and availability onto the sticky one. */
  #syncFromMainButton(main) {
    if (!main) return;
    const text = (main.textContent || "").replace(/\s+/g, " ").trim();
    // The theme renders both the idle and the "Added" label inside the button;
    // keep only the first so the bar doesn't read "Add to cart Added".
    const label = text.split(/\s+(?=Added$)/)[0] || text;
    this.#setText(this.#label, label);

    const disabled = main.disabled || main.getAttribute("aria-disabled") === "true";
    const button = this.#button;
    if (button && button.disabled !== disabled) {
      button.disabled = disabled;
      button.setAttribute("aria-disabled", String(disabled));
    }
  }

  /**
   * Mirror whichever price the customer is actually about to pay, reading it
   * from the page so money formatting stays Shopify's.
   *
   * The real button is the source of truth for both the price and the label:
   * if it says "Add subscription to cart" the customer pays Loop's discounted
   * subscription price (5% off, shown on the aria-checked option card); in
   * every other case they pay the plain variant price on the PDP.
   *
   * Tying the two together matters because Loop's widget goes stale: changing
   * pack size updates the PDP price but leaves Loop's cards showing the old
   * variant's prices indefinitely, while quietly reverting the selection to
   * one-time (reproduced on the live theme with no sticky bar present, so it
   * predates this component). Keying off the button means that when Loop drops
   * to one-time we follow it to the correct PDP price instead of echoing a
   * stale subscription figure.
   */
  #syncPrice(main = this.#watchedButton) {
    const isSubscription = /subscription/i.test(main?.textContent || "");
    const loopPrice = document.querySelector(
      '.loop-widget-purchase-option[aria-checked="true"] .loop-widget-purchase-option-price'
    );
    const pdpPrice = document.querySelector(
      '.product-information .price__current, .product-information [class*="price"] .money, .product-information .price'
    );
    const source = (isSubscription && loopPrice) || pdpPrice || loopPrice;
    if (!source) return;
    const priceText = source.textContent.replace(/\s+/g, " ").trim();
    this.#setText(this.#price, priceText);

    // Struck-through "was" price. On a subscription that is Loop's compare-at
    // (the 5% saving); otherwise it is the product's own sale compare-at, if
    // any. Loop's one-time card carries a $0.00 compare-at, so only show a
    // value that actually parses higher than what they are paying.
    const compareSource = isSubscription
      ? document.querySelector(
          '.loop-widget-purchase-option[aria-checked="true"] .loop-widget-purchase-option-compare-at-price'
        )
      : document.querySelector('.product-information s.compare-at-price');
    const compareText = compareSource
      ? compareSource.textContent.replace(/\s+/g, " ").trim()
      : "";
    const compare = this.#amount(compareText);
    const price = this.#amount(priceText);
    if (compare && price && compare > price) {
      this.#setText(this.#compare, compareText);
      this.#setHidden(this.#compare, false);
    } else {
      this.#setHidden(this.#compare, true);
    }
  }

  #show() {
    this.hidden = false;
    // next frame, so the transform transition actually runs
    requestAnimationFrame(() => this.setAttribute("open", ""));
  }

  #hide() {
    this.removeAttribute("open");
  }

  /** Watch the real button and toggle the bar on its visibility. */
  #watch() {
    const main = this.#findMainButton();
    if (!main) {
      // Loop can leave the page with no add-to-cart button at all for a frame
      // or two while it swaps its widget in. Drop the observer rather than
      // early-returning: it would still be watching the detached node, and a
      // detached element never intersects, so the bar would sit there open on
      // top of an on-screen real button until the next mutation healed it.
      this.#observer?.disconnect();
      this.#observer = null;
      this.#watchedButton = null;
      this.#hide();
      return;
    }
    if (main === this.#watchedButton) return;

    this.#watchedButton = main;
    this.#syncFromMainButton(main);
    this.#syncPrice();

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.#hide() : this.#show()),
      { rootMargin: "0px 0px -8px 0px" }
    );
    this.#observer.observe(main);
  }

  /** Forward the tap. The real button owns what actually gets added. */
  forwardToMainButton(event) {
    event?.preventDefault();
    const main = this.#findMainButton();
    if (!main) return;
    this.#syncFromMainButton(main);
    if (main.disabled) return;
    main.click();
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#watch();

    // Loop injects and replaces its button asynchronously, so we have to watch
    // the document. Mutations originating inside this bar are ignored and the
    // handler is coalesced to one run per frame, otherwise our own writes feed
    // straight back in.
    this.#mutationObserver = new MutationObserver((records) => {
      if (records.every((r) => this.contains(r.target))) return;
      if (this.#pendingFrame) return;
      this.#pendingFrame = requestAnimationFrame(() => {
        this.#pendingFrame = 0;
        this.#watch();
        this.#syncFromMainButton(this.#watchedButton);
        this.#syncPrice(this.#watchedButton);
      });
    });
    this.#mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      // Switching between "Subscribe & save" and "One-time purchase" only flips
      // aria-checked on Loop's option cards; without this the bar keeps showing
      // the previously selected option's price.
      attributes: true,
      attributeFilter: ["aria-checked"],
    });

    document.addEventListener(
      ThemeEvents.variantUpdate,
      () => {
        this.#watchedButton = null; // force re-resolve, markup may have been replaced
        this.#watch();
        this.#syncPrice();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#pendingFrame) cancelAnimationFrame(this.#pendingFrame);
  }
}

if (!customElements.get("floating-add-to-cart")) {
  customElements.define("floating-add-to-cart", FloatingAddToCartComponent);
}
